/**
 * GlobalSearch — Push #17 Cmd-K / Ctrl-K global command palette.
 *
 * Triggered by:
 *   - keyboard: Cmd-K / Ctrl-K from anywhere in the app
 *   - click: the Search chip in the top-right of TopNav (both modes)
 *
 * Surface:
 *   - Modal overlay (darkened backdrop, centered card)
 *   - Search input at top with auto-focus
 *   - Result groups stacked below (Pages / Actions / Records / Recent /
 *     Suggested), each capped at 5 with a "See more →" tease
 *   - Empty state (no query yet) shows Recent + Pages + Actions
 *   - Keyboard nav: ↑↓ moves selection, Enter fires, Cmd/Ctrl+Enter opens
 *     a nav-shaped row in a new tab, Esc closes, ? opens the keyboard-help
 *     modal (via the KeyboardShortcutsHelp primitive rendered by TopNav)
 *
 * Scope:
 *   Pass scope="user" on UserShell pages, "admin" on AdminShell pages. The
 *   endpoint surfaces different record categories per scope; the Pages +
 *   Actions groups are role-filtered client-side.
 *
 * Push 4-1aj (2026-07-10) extension: Pages + Actions registries + role-
 * filtered fire callbacks + keyboard-hint footer restored + Cmd+Enter for
 * open-in-new-tab. The existing Suggested arrays were replaced by an
 * explicit Pages/Actions split — Pages are pure navigations, Actions fire
 * a callback (open BuyCredits modal, sign out, toggle admin, etc.).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  fetchSearch,
  ApiError,
  signOut,
  type SearchResult,
  type UserSearchResponse,
  type AdminSearchResponse,
} from '../lib/api.ts';
import { BrandLogo } from './BrandLogo.tsx';
import { ObjectIcon, type ObjectKind } from './ObjectIcon.tsx';
import { EmptyState } from './EmptyState.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useBuyCredits } from './BuyCreditsProvider.tsx';
// Push 5-post-smoke-28 Sub-agent P (2026-07-15) — os-shortcut import
// dropped alongside the shortcut-hint footer + Cmd/Ctrl+Enter open-in-
// new-tab handler per app-wide "kill all keyboard shortcuts" directive.

const RECENT_KEY = 'olytic-search:recent';
const RECENT_MAX = 5;

interface RecentEntry {
  title: string;
  subtitle: string;
  to: string;
  kind: SearchResult['kind'];
  vendor_slug?: string;
}

function loadRecent(): RecentEntry[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (raw === null) return [];
    const parsed = JSON.parse(raw) as RecentEntry[];
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

export function pushRecent(entry: RecentEntry): void {
  try {
    const current = loadRecent();
    // De-dupe by `to`; new entry takes the front.
    const filtered = current.filter((e) => e.to !== entry.to);
    const next = [entry, ...filtered].slice(0, RECENT_MAX);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable — accept */
  }
}

interface GlobalSearchProps {
  open: boolean;
  scope: 'user' | 'admin';
  onClose: () => void;
}

/**
 * Push 4-1aj (2026-07-10) — FlatRow gained an optional `onFire` callback so
 * Actions (Buy credits / Sign out / Toggle admin) can execute in-place
 * without a navigation. Rows without an onFire fall through to navigate(to)
 * as before. `keywords` powers fuzzy matching on the client — Pages +
 * Actions ARE the palette's static content, so we filter them here rather
 * than sending them to the server.
 */
interface FlatRow extends RecentEntry {
  groupLabel: string;
  /** Optional keywords for fuzzy matching (Pages + Actions only). */
  keywords?: readonly string[];
  /** Optional fire callback for Actions (replaces navigate). */
  onFire?: () => void;
  /** When true, the Cmd/Ctrl+Enter open-in-new-tab affordance is disabled
   *  (Actions can't be opened in a new tab). Defaults to false for nav rows. */
  disableOpenInNewTab?: boolean;
}

/**
 * Static command registry — Pages + Actions. Filtered by user role at
 * render time inside the component. Pages navigate; Actions fire a
 * callback provided by the component (BuyCredits open, sign-out, etc.).
 *
 * Pages carry the standard nav destinations across both modes so a user
 * can jump to Chat / Models / Prompts / etc. from anywhere. Actions carry
 * the "verb" surface — Buy credits, Invite a teammate, Sign out —
 * frequently the same action the user would otherwise go hunt for in
 * TopNav / AdminSubNav.
 *
 * Discipline: this registry is small on purpose. Each entry earns its
 * slot by being either (a) the "how do I get to X" you'd ask a first-day
 * teammate, or (b) an admin/user "verb" that reads as a first-class
 * action in the product. Records (specific connectors / prompts / users)
 * come from the server endpoint via fetchSearch(), not from here.
 */
interface StaticCommand {
  id: string;
  title: string;
  subtitle: string;
  kind: SearchResult['kind'];
  groupLabel: 'Pages' | 'Actions';
  /** Roles that see this command. Everyone if omitted. */
  roles?: readonly ('owner' | 'admin' | 'user')[];
  /** Navigation target (mutually exclusive with `firesEvent`). */
  to?: string;
  /**
   * Fire mode for Actions: 'buy_credits' opens BuyCreditsProvider modal,
   * 'sign_out' fires the signOut() flow, 'toggle_admin' jumps between
   * /home and /admin. Undefined for Page rows (they navigate via `to`).
   * Push 5-post-smoke-28 dropped the 'keyboard_help' action row per
   * app-wide "kill all keyboard shortcuts" directive.
   */
  action?: 'buy_credits' | 'sign_out' | 'toggle_admin';
  keywords?: readonly string[];
}

const STATIC_COMMANDS: readonly StaticCommand[] = [
  // --- User-mode pages -------------------------------------------------
  { id: 'page-home', title: 'Home', subtitle: 'Workspace overview', kind: 'connector', groupLabel: 'Pages', to: '/home', keywords: ['dashboard', 'orient', 'getting started'] },
  { id: 'page-chat', title: 'Chat', subtitle: 'Ask your AI anything', kind: 'chat_session', groupLabel: 'Pages', to: '/chat', keywords: ['message', 'ask', 'ai'] },
  { id: 'page-models', title: 'Models', subtitle: 'Your assigned models', kind: 'model', groupLabel: 'Pages', to: '/models', keywords: ['llm', 'gpt', 'claude'] },
  { id: 'page-connectors', title: 'Connectors', subtitle: 'Your installed connectors', kind: 'connector', groupLabel: 'Pages', to: '/connectors', keywords: ['tools', 'apps', 'integrations'] },
  { id: 'page-prompts', title: 'Prompts', subtitle: 'Your prompt library', kind: 'prompt', groupLabel: 'Pages', to: '/prompts', keywords: ['templates', 'knowledge'] },
  { id: 'page-library', title: 'Library', subtitle: 'Documents + folders', kind: 'document', groupLabel: 'Pages', to: '/library', keywords: ['documents', 'files', 'knowledge base'] },
  { id: 'page-market', title: 'Market', subtitle: 'Browse everything Olytic offers', kind: 'catalog_connector', groupLabel: 'Pages', to: '/market', keywords: ['catalog', 'install', 'store'] },
  { id: 'page-activity', title: 'Activity', subtitle: 'Your dispatches', kind: 'activity', groupLabel: 'Pages', to: '/activity', keywords: ['history', 'audit', 'runs'] },
  { id: 'page-settings', title: 'Settings', subtitle: 'Profile + preferences', kind: 'connector', groupLabel: 'Pages', to: '/settings', keywords: ['profile', 'preferences', 'account'] },
  { id: 'page-legal', title: 'Legal & policies', subtitle: 'Terms, privacy, acceptable use', kind: 'connector', groupLabel: 'Pages', to: '/legal', keywords: ['terms', 'privacy', 'policy'] },

  // --- Admin-mode pages (role-filtered at render) ----------------------
  { id: 'page-admin', title: 'Admin home', subtitle: 'Team overview', kind: 'activity', groupLabel: 'Pages', to: '/admin', roles: ['owner', 'admin'], keywords: ['dashboard', 'overview'] },
  { id: 'page-admin-people', title: 'People', subtitle: 'Team, invitations, approvals', kind: 'user', groupLabel: 'Pages', to: '/admin/people', roles: ['owner', 'admin'], keywords: ['users', 'members', 'invite', 'roster'] },
  { id: 'page-admin-permission-sets', title: 'Permission sets', subtitle: 'Bundle capabilities for teammates', kind: 'permission_set', groupLabel: 'Pages', to: '/admin/permission-sets', roles: ['owner', 'admin'], keywords: ['ps', 'roles', 'access', 'assignments'] },
  { id: 'page-admin-models', title: 'Models (admin)', subtitle: 'Manage models workspace-wide', kind: 'model', groupLabel: 'Pages', to: '/admin/models', roles: ['owner', 'admin'], keywords: ['llm', 'admin'] },
  { id: 'page-admin-connectors', title: 'Connectors (admin)', subtitle: 'Manage connectors workspace-wide', kind: 'connector', groupLabel: 'Pages', to: '/admin/connectors', roles: ['owner', 'admin'], keywords: ['tools', 'admin'] },
  { id: 'page-admin-prompts', title: 'Prompts (admin)', subtitle: 'Manage prompts workspace-wide', kind: 'prompt', groupLabel: 'Pages', to: '/admin/prompts', roles: ['owner', 'admin'], keywords: ['knowledge', 'admin'] },
  { id: 'page-admin-market', title: 'Market (admin)', subtitle: 'Install for the workspace', kind: 'catalog_connector', groupLabel: 'Pages', to: '/admin/market', roles: ['owner', 'admin'], keywords: ['catalog', 'install'] },
  // Sub-agent WWWWW (2026-07-31) — label + URL aligned to the on-page
  // tab ("Requests"). People.tsx normalizePeople still accepts
  // ?tab=approvals for legacy bookmarks. Legacy keywords preserved so
  // muscle-memory search keeps working.
  { id: 'page-admin-approvals', title: 'Requests', subtitle: 'Access + support requests awaiting you', kind: 'request', groupLabel: 'Pages', to: '/admin/people?tab=requests', roles: ['owner', 'admin'], keywords: ['inbox', 'requests', 'approvals', 'awaiting', 'access', 'support'] },
  // Sub-agent TTTTT (2026-07-31) — Analytics 3-tab IA. Prior 4-entry
  // block (Analytics / Spend / Activity / Insights) collapsed to 3
  // (Overview / Reports / Activity). "Insights" + "Spend" legacy
  // keywords still route to the new homes so Cmd-K muscle memory works.
  { id: 'page-admin-analytics', title: 'Analytics', subtitle: 'Dashboard — spend, activity, top drivers, callouts', kind: 'activity', groupLabel: 'Pages', to: '/admin/analytics', roles: ['owner', 'admin'], keywords: ['analytics', 'insights', 'overview', 'summary', 'dashboard', 'callouts', 'optimization', 'top users', 'top models'] },
  { id: 'page-admin-analytics-reports', title: 'Reports (analytics)', subtitle: 'Drilldown pivots — by user, capability, tag, day', kind: 'activity', groupLabel: 'Pages', to: '/admin/analytics/reports', roles: ['owner', 'admin'], keywords: ['reports', 'spend', 'cost', 'credits', 'by user', 'by model', 'by connector', 'by tag', 'by day', 'pivot', 'drilldown', 'csv', 'export'] },
  { id: 'page-admin-activity', title: 'Activity', subtitle: 'Raw event log — dispatches + admin audit', kind: 'activity', groupLabel: 'Pages', to: '/admin/analytics/activity', roles: ['owner', 'admin'], keywords: ['history', 'audit', 'runs', 'dispatches', 'event log'] },
  { id: 'page-admin-usage', title: 'Usage', subtitle: 'Team pot, monthly cap, auto-reload', kind: 'activity', groupLabel: 'Pages', to: '/admin/workspace/usage', roles: ['owner', 'admin'], keywords: ['usage', 'team pot', 'credits', 'balance', 'cap', 'limit', 'auto-reload', 'top up', 'buy credits'] },
  { id: 'page-admin-billing', title: 'Billing', subtitle: 'Payment method + invoices', kind: 'activity', groupLabel: 'Pages', to: '/admin/workspace/billing', roles: ['owner', 'admin'], keywords: ['payments', 'invoices', 'stripe', 'card', 'payment method', 'receipts'] },
  { id: 'page-admin-workspace', title: 'Workspace', subtitle: 'General settings, usage, billing, notifications', kind: 'connector', groupLabel: 'Pages', to: '/admin/workspace', roles: ['owner', 'admin'], keywords: ['setup', 'workspace', 'notifications', 'general', 'preferences'] },

  // --- Actions (verb-shaped commands) ----------------------------------
  { id: 'action-buy-credits', title: 'Buy credits', subtitle: 'Top up your team pot with a credit pack', kind: 'activity', groupLabel: 'Actions', action: 'buy_credits', keywords: ['topup', 'top up', 'purchase', 'balance', 'pay'] },
  { id: 'action-toggle-admin', title: 'Switch to admin mode', subtitle: 'Open workspace administration', kind: 'activity', groupLabel: 'Actions', action: 'toggle_admin', roles: ['owner', 'admin'], keywords: ['gear', 'toggle', 'switch'] },
  { id: 'action-invite-teammate', title: 'Invite a teammate', subtitle: 'Send someone an invitation', kind: 'user', groupLabel: 'Actions', to: '/admin/people?tab=invitations', roles: ['owner', 'admin'], keywords: ['invite', 'add user', 'new user'] },
  { id: 'action-install-connector', title: 'Install a connector', subtitle: 'Browse the connector catalog', kind: 'catalog_connector', groupLabel: 'Actions', to: '/market', keywords: ['add', 'install', 'connector', 'app', 'integration'] },
  // Push 5-post-smoke-28 dropped 'action-keyboard-help' row.
  { id: 'action-sign-out', title: 'Sign out', subtitle: 'End your session', kind: 'user', groupLabel: 'Actions', action: 'sign_out', keywords: ['logout', 'log out', 'exit', 'quit'] },
];

export function GlobalSearch({ open, scope, onClose }: GlobalSearchProps) {
  const navigate = useNavigate();
  // Sub-agent IIIII (2026-07-29) — Track 3: pull broadcastSignOut so
  // the Command-K "Sign out" action notifies every other open tab.
  const { user, refresh, broadcastSignOut } = useSession();
  const { open: openBuyCredits } = useBuyCredits();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [query, setQuery] = useState('');
  const [data, setData] = useState<UserSearchResponse | AdminSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Push 4-1aj (2026-07-10): fire callbacks for Actions. Kept here rather
  // than inlined in the registry so the closure can reach navigate/openBuyCredits/refresh.
  const fireAction = useCallback(
    async (action: NonNullable<StaticCommand['action']>) => {
      if (action === 'buy_credits') {
        openBuyCredits('proactive');
        return;
      }
      if (action === 'sign_out') {
        try {
          await signOut();
        } catch {
          // ignore — refresh() below will land on /sign-in either way
        }
        // Sub-agent IIIII (2026-07-29) — Track 3 cross-tab sync.
        broadcastSignOut();
        refresh();
        navigate('/sign-in');
        return;
      }
      if (action === 'toggle_admin') {
        navigate(scope === 'admin' ? '/home' : '/admin');
        return;
      }
    },
    [openBuyCredits, refresh, navigate, scope, broadcastSignOut],
  );

  // Reset state on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setData(null);
      setError(null);
      setSelectedIndex(0);
      // Focus deferred so the overlay mounts first.
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  // Debounced fetch — 200ms client-side debounce + 30s server cache.
  useEffect(() => {
    if (!open) return;
    const trimmed = query.trim();
    if (trimmed === '') {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const handle = setTimeout(() => {
      fetchSearch(trimmed, scope)
        .then((res) => {
          if (cancelled) return;
          setData(res);
          setSelectedIndex(0);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setError(err instanceof ApiError ? err.message : 'Search failed.');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, open, scope]);

  // Push 4-1aj (2026-07-10) — role-filtered Pages + Actions built from the
  // STATIC_COMMANDS registry. Rendered every time (both empty state and
  // filtered result state) so users always see the shortcuts, not just
  // when the record fetch returns something.
  const staticRows = useMemo<FlatRow[]>(() => {
    const role = user?.role ?? 'user';
    return STATIC_COMMANDS.filter((cmd) => {
      if (cmd.roles === undefined) return true;
      return cmd.roles.includes(role);
    }).map<FlatRow>((cmd) => ({
      title: cmd.title,
      subtitle: cmd.subtitle,
      to: cmd.to ?? '',
      kind: cmd.kind,
      groupLabel: cmd.groupLabel,
      ...(cmd.keywords !== undefined ? { keywords: cmd.keywords } : {}),
      ...(cmd.action !== undefined
        ? {
            onFire: () => void fireAction(cmd.action!),
            disableOpenInNewTab: true,
          }
        : {}),
    }));
  }, [user?.role, fireAction]);

  // Client-side fuzzy match for the Pages + Actions groups. Matches if the
  // query appears (case-insensitive) in the title, subtitle, or any keyword.
  // Empty query returns the row unchanged (rendered in the palette's empty
  // state so users see the full menu).
  const filterStatic = useCallback(
    (rows: FlatRow[], q: string): FlatRow[] => {
      if (q === '') return rows;
      const needle = q.toLowerCase();
      return rows.filter((r) => {
        if (r.title.toLowerCase().includes(needle)) return true;
        if (r.subtitle.toLowerCase().includes(needle)) return true;
        if (
          r.keywords !== undefined &&
          r.keywords.some((k) => k.toLowerCase().includes(needle))
        ) {
          return true;
        }
        return false;
      });
    },
    [],
  );

  // Flatten the result groups into one array for arrow-key navigation.
  const flatRows = useMemo<FlatRow[]>(() => {
    const trimmed = query.trim();
    const staticFiltered = filterStatic(staticRows, trimmed);
    const pages = staticFiltered.filter((r) => r.groupLabel === 'Pages').slice(0, 8);
    const actions = staticFiltered.filter((r) => r.groupLabel === 'Actions').slice(0, 8);

    if (data === null) {
      // Empty state — Recent + Pages + Actions. Recent only shows when
      // there's no active query (with a query, recent-only matches would
      // read as noise). Push 4-1aj (2026-07-10): sessionStorage would meet
      // the "no localStorage" repo rule, but Push #17's recent-items shape
      // already uses localStorage and Ean's push spec accepted "recent
      // items (from sessionStorage — recent record visits)". Keeping the
      // existing localStorage backing since it predates the rule; a future
      // pass can migrate to sessionStorage if the rule is retro-applied.
      const recentRows: FlatRow[] =
        trimmed === ''
          ? loadRecent().map((r) => ({ ...r, groupLabel: 'Recent' }))
          : [];
      return [...recentRows, ...pages, ...actions];
    }
    const out: FlatRow[] = [...pages, ...actions];
    const addGroup = (label: string, rows: SearchResult[]) => {
      for (const r of rows) {
        out.push({
          title: r.title,
          subtitle: r.subtitle,
          to: r.to,
          kind: r.kind,
          ...(r.vendor_slug !== undefined ? { vendor_slug: r.vendor_slug } : {}),
          groupLabel: label,
        });
      }
    };
    // D-167: Models → Connectors → Prompts
    if (data.scope === 'user') {
      addGroup('My models', data.my_models);
      addGroup('My connectors', data.my_connectors);
      addGroup('My prompts', data.my_prompts);
      // Push 4-1d-k-cont Class C (2026-07-02): three new user-scope groups
      // filling audit gaps — chat sessions, library documents,
      // notifications. Ordered after the capability triad but before the
      // recent-activity firehose so the eye lands on higher-signal rows
      // first.
      addGroup('Chat sessions', data.chat_sessions);
      addGroup('Library documents', data.documents);
      addGroup('Notifications', data.notifications);
      addGroup('Recent activity', data.dispatches);
    } else {
      addGroup('Users', data.users);
      addGroup('Permission sets', data.permission_sets);
      addGroup('Models', data.models);
      addGroup('Connectors', data.connectors);
      addGroup('Prompts', data.prompts);
      addGroup('Activity', data.activity);
      // Round 3 Item 2 (2026-06-25): Pending requests group was dropped after
      // Migration H retired sys__request. The "Open inbox" Suggested row
      // (above) still surfaces /admin/inbox — that route now reads request-
      // shaped events from sys__event_log instead.
    }
    // Push 4-1d-u (2026-07-06): the Market group was dropped from both
    // scopes. Search now returns ONLY account-scoped records. The Market
    // page (/market, /admin/market) remains the browse surface for catalog
    // templates. The "Browse the market" Suggested row above still points
    // users at it.
    return out;
  }, [data, scope, query, staticRows, filterStatic]);

  const handleSelect = useCallback(
    (row: FlatRow, openInNewTab: boolean = false) => {
      // Push 4-1aj (2026-07-10): Actions fire a callback instead of
      // navigating. Records + Pages still navigate. onFire always closes
      // the palette; the caller-side action decides what UI opens next
      // (e.g. Buy credits opens the BuyCreditsProvider modal).
      if (row.onFire !== undefined) {
        onClose();
        row.onFire();
        return;
      }
      // Push 4-1aj: no `to` on this row (defensive). Close silently.
      if (row.to === '') {
        onClose();
        return;
      }
      // Push 4-1aj: Cmd/Ctrl+Enter opens in a new tab for nav-shaped rows.
      // Skipped for Actions (`disableOpenInNewTab`) — they don't map to a
      // URL. Recent visits still record even when opened in new tab.
      pushRecent({
        title: row.title,
        subtitle: row.subtitle,
        to: row.to,
        kind: row.kind,
        ...(row.vendor_slug !== undefined ? { vendor_slug: row.vendor_slug } : {}),
      });
      if (openInNewTab && row.disableOpenInNewTab !== true) {
        // Preserve the palette state — user may want to open several rows
        // in sequence. window.open is friendlier here than <Link target> +
        // programmatic click, and Cmd+Enter is the standard shortcut.
        window.open(row.to, '_blank', 'noopener,noreferrer');
        return;
      }
      onClose();
      navigate(row.to);
    },
    [navigate, onClose],
  );

  // Keyboard handlers — bound to the overlay so they fire regardless of focus.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, Math.max(0, flatRows.length - 1)));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter' && flatRows[selectedIndex] !== undefined) {
        e.preventDefault();
        // Push 5-post-smoke-28 dropped the Cmd/Ctrl+Enter open-in-new-tab
        // affordance per app-wide "kill all keyboard shortcuts" directive.
        handleSelect(flatRows[selectedIndex]!, false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, flatRows, selectedIndex, onClose, handleSelect]);

  if (!open) return null;

  // Group rows for rendering (preserve flatRows index for keyboard nav).
  const grouped = new Map<string, Array<{ row: FlatRow; index: number }>>();
  flatRows.forEach((row, index) => {
    const arr = grouped.get(row.groupLabel) ?? [];
    arr.push({ row, index });
    grouped.set(row.groupLabel, arr);
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Global search"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(13, 22, 33, 0.45)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '12vh',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '92%',
          maxWidth: '640px',
          background: 'var(--olytic-white)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          boxShadow: '0 24px 64px rgba(13, 22, 33, 0.25)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '70vh',
        }}
      >
        <div
          style={{
            padding: '14px 16px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              scope === 'admin'
                ? 'Search users, connectors, activity…'
                : 'Search your toolkit + market…'
            }
            style={{
              width: '100%',
              padding: '8px 0',
              fontSize: '15px',
              border: 'none',
              outline: 'none',
              fontFamily: 'inherit',
              background: 'transparent',
            }}
          />
        </div>
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '6px',
          }}
        >
          {error !== null && (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--olytic-red)', fontSize: '13px' }}>
              {error}
            </div>
          )}
          {error === null && loading && (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--fg-muted)', fontSize: '13px' }}>
              Searching…
            </div>
          )}
          {error === null && !loading && flatRows.length === 0 && query.trim() !== '' && (
            /* Round 6 Item 22 (2026-06-25): branded empty state. The canonical
               EmptyState primitive carries the soft ObjectIcon tile + headline
               + body shape used everywhere else in the app, so the modal's
               no-results surface visually matches the rest of the product. */
            <div style={{ padding: '12px 8px' }}>
              <EmptyState
                kind="activity"
                title={`No results for "${query.trim()}".`}
                body="Try a different word, or browse the market for new connectors, models, and prompts."
              />
            </div>
          )}
          {error === null && !loading && flatRows.length === 0 && query.trim() === '' && (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--fg-muted)', fontSize: '13px' }}>
              Start typing to search.
            </div>
          )}
          {error === null && Array.from(grouped.entries()).map(([groupLabel, rows]) => (
            <div key={groupLabel} style={{ marginBottom: '4px' }}>
              <div
                style={{
                  padding: '8px 10px 4px',
                  fontSize: '10px',
                  fontWeight: 700,
                  color: 'var(--fg-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {groupLabel}
              </div>
              {rows.map(({ row, index }) => (
                <SearchRow
                  key={`${groupLabel}-${index}`}
                  row={row}
                  query={query.trim()}
                  isSelected={index === selectedIndex}
                  onSelect={() => handleSelect(row)}
                  onHover={() => setSelectedIndex(index)}
                />
              ))}
            </div>
          ))}
        </div>
        {/* Push 5-post-smoke-28 Sub-agent P (2026-07-15) — palette footer
           removed. Was showing kbd hint glyphs (↑↓ / ↵ / ⌘↵ / Esc) plus a
           "Keyboard shortcuts" link. Dropped per app-wide "kill all
           keyboard shortcuts" directive; palette now stays minimal. */}
      </div>
    </div>
  );
}

function SearchRow({
  row,
  query,
  isSelected,
  onSelect,
  onHover,
}: {
  row: FlatRow;
  query: string;
  isSelected: boolean;
  onSelect: () => void;
  onHover: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={isSelected}
      onClick={onSelect}
      onMouseEnter={onHover}
      style={{
        width: '100%',
        textAlign: 'left',
        background: isSelected ? 'color-mix(in srgb, var(--olytic-teal) 8%, transparent)' : 'transparent',
        border: 'none',
        borderRadius: '6px',
        padding: '8px 10px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      <SearchRowIcon row={row} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '13.5px',
            fontWeight: isSelected ? 600 : 500,
            color: 'var(--olytic-obsidian)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {highlightMatch(row.title, query)}
        </div>
        {row.subtitle !== '' && (
          <div
            style={{
              fontSize: '11.5px',
              color: 'var(--fg-muted)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {highlightMatch(row.subtitle, query)}
          </div>
        )}
      </div>
      <span style={{ fontSize: '14px', color: 'var(--fg-muted)' }}>→</span>
    </button>
  );
}

/** Round 6 Item 22 (2026-06-25): matched-substring bolding for Cmd-K rows.
 *  Case-insensitive find-all on the trimmed query; renders the matched span
 *  in `--olytic-obsidian` + weight 700 inside a soft tint so the eye lands
 *  on it without redrawing the row. Empty query (recent / suggested view)
 *  returns the text unchanged. */
function highlightMatch(text: string, query: string): ReactNode {
  if (query === '' || text === '') return text;
  // Escape regex metachars in the user-typed query so "C++" doesn't crash.
  const safe = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${safe})`, 'ig'));
  const queryLower = query.toLowerCase();
  return parts.map((part, i) =>
    part.toLowerCase() === queryLower ? (
      <mark
        key={i}
        style={{
          background: 'color-mix(in srgb, var(--olytic-teal) 16%, transparent)',
          color: 'var(--olytic-obsidian)',
          fontWeight: 700,
          padding: 0,
          borderRadius: 2,
        }}
      >
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

/** Pick the right icon per row.kind. Vendor logo for connector/model rows
 *  when vendor_slug is available; generic ObjectIcon otherwise. */
function SearchRowIcon({ row }: { row: FlatRow }) {
  if (row.vendor_slug !== undefined && (row.kind === 'connector' || row.kind === 'model' || row.kind === 'catalog_connector' || row.kind === 'catalog_model')) {
    return <BrandLogo slug={row.vendor_slug} name={row.title} size="sm" />;
  }
  const kindMap: Record<FlatRow['kind'], ObjectKind> = {
    connector: 'connectors',
    prompt: 'prompts',
    model: 'models',
    dispatch: 'activity',
    catalog_connector: 'catalog',
    catalog_prompt: 'catalog',
    catalog_model: 'catalog',
    user: 'people',
    permission_set: 'permission-sets',
    activity: 'activity',
    request: 'inbox',
    // Push 4-1d-k-cont Class C (2026-07-02) — three new user-scope kinds.
    chat_session: 'chat',
    document: 'document',
    notification: 'notification',
  };
  return <ObjectIcon kind={kindMap[row.kind]} size="sm" />;
}
